import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { getLoginReturnTo } from "./utils/authNavigation";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./firebase";
import ThemeCustomization from "./themes";
import Header from "./components/Header";
import MobileBottomNav, { MOBILE_NAV_HEIGHT } from "./components/MobileBottomNav";
import { SnackbarProvider } from "notistack";
import { Toolbar } from "@mui/material";
import Box from "@mui/material/Box";
import StartFooter from "./pages/StartFooter";
import ScrollToTop from "./components/ScrollToTop";
import { Loader, PageLoading } from "./components/loader";
import ErrorBoundary from "./components/ErrorBoundary";
import { features } from "./utils/features";

const PrivateRoute: React.FC<{ children: JSX.Element; user: any }> = ({ children, user }) => {
  const location = useLocation();
  return user ? children : <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search + location.hash }} />;
};

const Login = lazy(() => import("./pages/auth/LoginWrapper"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPasswordWrapper"));
// webpackPreload tells the browser to fetch this chunk in parallel with the
// main bundle (it's the "/" route almost every visit lands on first), instead
// of only starting the fetch after the component tries to render.
const Home = lazy(() => import(/* webpackPreload: true */ "./pages/Home"));
const HomeV2 = lazy(() => import("./pages/HomeV2"));
const HomeV3 = lazy(() => import("./pages/HomeV3"));
// Prefetched once the app is up: nearly every visit goes on to the results or the search form,
// and a page whose code is already here opens at once, even on a weak connection.
const SearchResult = lazy(() => import(/* webpackPrefetch: true */ "./pages/OffersPage"));
const CreateSearchCriteria = lazy(() => import(/* webpackPrefetch: true */ "./pages/SearchCriteria"));
const Favorite = lazy(() => import("./pages/Favorite"));
const Settings = lazy(() => import("./pages/Settings"));
const History = lazy(() => import("./pages/History"));
const Wallet = lazy(() => import("./pages/Wallet"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const ContactForm = lazy(() => import("./components/forms/ContactForm"));
const SearchSimilar = lazy(() => import("./pages/SearchSimilar"));
const SimilarOffers = lazy(() => import("./pages/SimilarOffers"));
const AddAgency = lazy(() => import("./pages/AddAgency"));
const Agencies = lazy(() => import("./pages/Agencies"));
const Saved = lazy(() => import("./pages/Saved"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Conversations = lazy(() => import("./pages/Conversations"));
const Sell = lazy(() => import("./pages/Sell"));
const ListingForm = lazy(() => import("./pages/ListingForm"));
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const MyListings = lazy(() => import("./pages/MyListings"));
const ListingsBrowse = lazy(() => import("./pages/ListingsBrowse"));
const ModerationListings = lazy(() => import("./pages/ModerationListings"));
const SiteHealth = lazy(() => import("./pages/SiteHealth"));

const App = () => {
  const [user, loading] = useAuthState(auth);
  const location = useLocation();

  if (loading) {
    return null;
  }

  return (
    <ThemeCustomization>
      <SnackbarProvider
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            minHeight: "100vh",
            bgcolor: "var(--bg)",
          }}
        >
          <Header />

          <Box
            component="main"
            sx={{
              width: "100%",
              flexGrow: 1,
              bgcolor: "var(--bg)",
            }}
          >
            <Toolbar />
            <Loader />
            {/* One error, or one page whose code would not download, must not blank the whole app. */}
            <ErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<PageLoading />}>
                <ScrollToTop />
                <Routes>
                <Route path="/login" element={user ? <Navigate to={getLoginReturnTo(location.state)} replace /> : <Login />} />
                <Route path="/register" element={user ? <Navigate to={getLoginReturnTo(location.state)} replace /> : <Login mode="register" />} />
                <Route path="/resetPassword" element={user ? <Navigate to={getLoginReturnTo(location.state)} replace /> : <ResetPassword />} />
                <Route path="/favorite" element={<PrivateRoute user={user}><Favorite /></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute user={user}><Settings /></PrivateRoute>} />
                <Route path="/history" element={<PrivateRoute user={user}><History /></PrivateRoute>} />
                <Route path="/contact" element={<ContactForm />} />
                <Route path="/searchResult/:id" element={<SearchResult />} />
                <Route path="/searchResult/:id/:slug" element={<SearchResult />} />
                <Route path="/search-similar" element={<PrivateRoute user={user}><SearchSimilar /></PrivateRoute>} />
                <Route path="/search-criteria" element={<CreateSearchCriteria />} />
                <Route path="/similar" element={<PrivateRoute user={user}><SimilarOffers /></PrivateRoute>} />
                <Route path="/subscriptions" element={<PrivateRoute user={user}><Saved /></PrivateRoute>} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                {/* Routes left out of the release build fall through to "*" and land on the search. */}
                {features.marketplace && (
                  <>
                    <Route path="/wallet" element={<PrivateRoute user={user}><Wallet /></PrivateRoute>} />
                    <Route path="/sell" element={<Sell />} />
                    <Route path="/sell/new" element={<PrivateRoute user={user}><ListingForm /></PrivateRoute>} />
                    <Route path="/my-listings" element={<PrivateRoute user={user}><MyListings /></PrivateRoute>} />
                    <Route path="/my-listings/:id/edit" element={<PrivateRoute user={user}><ListingForm /></PrivateRoute>} />
                    <Route path="/listing/:id" element={<ListingDetail />} />
                    <Route path="/listings" element={<ListingsBrowse />} />
                  </>
                )}
                {features.messages && (
                  <>
                    <Route path="/conversations" element={<PrivateRoute user={user}><Conversations /></PrivateRoute>} />
                    <Route path="/conversations/:id" element={<PrivateRoute user={user}><Conversations /></PrivateRoute>} />
                  </>
                )}
                <Route path="/add-site" element={<AddAgency />} />
                {features.agencies && <Route path="/agency-directory" element={<Agencies />} />}
                {features.admin && (
                  <>
                    <Route path="/admin/listings" element={<PrivateRoute user={user}><ModerationListings /></PrivateRoute>} />
                    <Route path="/admin/sites" element={<PrivateRoute user={user}><SiteHealth /></PrivateRoute>} />
                  </>
                )}
                <Route path="/" element={<Home />} />
                {features.experiments && (
                  <>
                    <Route path="/v2" element={<HomeV2 />} />
                    <Route path="/v3" element={<HomeV3 />} />
                  </>
                )}
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
          </Box>
          <StartFooter />
          {/* The fixed mobile bar would otherwise cover the end of the page. */}
          <Box sx={{ height: { xs: `${MOBILE_NAV_HEIGHT}px`, md: 0 } }} />
          <MobileBottomNav />
        </Box>
      </SnackbarProvider>
    </ThemeCustomization>
  );
};

export default App;
